// src/api/customerAuthService.js
import {
  signOut,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  linkWithPopup
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  increment,
  limit,
  orderBy
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../utils/firebase';
import { assignVouchersForNewCustomer } from './salonApi';

// Constants
const CUSTOMER_EMAIL_KEY = 'emailForSignIn';

// Create customer document ID from email and salonId
const createCustomerId = (email, salonId) => {
  return `${email.toLowerCase()}_${salonId}`;
};

/**
 * Send a magic link to the customer's email
 * @param {string} email - Customer email address
 * @param {string} salonId - Salon identifier
 * @param {string} redirectUrl - URL to redirect after authentication
 */
export const sendMagicLink = async (email, salonId, redirectUrl) => {
  try {
    // Use the provided salonId, with fallback for invalid values
    let verifiedSalonId = salonId;

    if (!salonId || salonId === ':salonId') {
      console.warn("No valid salonId provided, using 'demo-salon' as fallback");
      verifiedSalonId = 'demo-salon';
    }

    const actionRedirectUrl = redirectUrl || `${window.location.origin}/salon/${verifiedSalonId}/verify-email?email=${email}`;
    console.log("Using redirect URL:", actionRedirectUrl);

    // Call Cloud Function to generate sign-in link and send branded email
    const sendCustomMagicLink = httpsCallable(functions, 'sendCustomMagicLink');
    await sendCustomMagicLink({
      email,
      salonId: verifiedSalonId,
      redirectUrl: actionRedirectUrl,
    });
    console.log("Custom magic link email sent successfully");

    // Save the email and verified salonId locally to use on sign-in
    localStorage.setItem(CUSTOMER_EMAIL_KEY, email);
    localStorage.setItem('salonId', verifiedSalonId);

    return {
      success: true,
      salonId: verifiedSalonId
    };
  } catch (error) {
    console.error('Error sending magic link:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    throw error;
  }
};

/**
 * Check if the current URL is a sign-in link
 */
export const isEmailSignInLink = () => {
  return isSignInWithEmailLink(auth, window.location.href);
};

/**
 * Complete the sign-in process with the email link
 * @param {string} email - Customer email (from localStorage or user input)
 * @param {string} salonId - Salon identifier (from localStorage or URL parameter)
 */
export const completeSignInWithEmailLink = async (email, salonId) => {
  try {
    // Confirm it's a sign-in with email link
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      throw new Error('Invalid email link');
    }

    // Sign in with email link
    const result = await signInWithEmailLink(
      auth,
      email,
      window.location.href
    );

    // Clear email from storage
    localStorage.removeItem(CUSTOMER_EMAIL_KEY);

    // Create or update customer profile
    await createOrUpdateCustomerProfile(result.user, salonId);

    // Return the user to be redirected to dashboard
    return result.user;
  } catch (error) {
    console.error('Error completing sign in with email link:', error);
    throw error;
  }
};
/**
 * Sign in with Google
 * @param {string} salonId - Salon identifier
 */
export const signInWithGoogle = async (salonId) => {
  try {
    const provider = new GoogleAuthProvider();
    // Force account selection every time
    provider.setCustomParameters({ prompt: 'select_account' });
    const result = await signInWithPopup(auth, provider);

    // Create or update customer profile
    await createOrUpdateCustomerProfile(result.user, salonId);

    return result.user;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw error;
  }
};

/**
 * Sign in with Facebook
 * @param {string} salonId - Salon identifier
 */
export const signInWithFacebook = async (salonId) => {
  try {
    const provider = new FacebookAuthProvider();
    // Force re-authentication to allow account selection
    provider.setCustomParameters({ auth_type: 'reauthenticate' });
    const result = await signInWithPopup(auth, provider);

    // Create or update customer profile
    await createOrUpdateCustomerProfile(result.user, salonId);

    return result.user;
  } catch (error) {
    console.error('Error signing in with Facebook:', error);
    throw error;
  }
};

/**
 * Sign in with Yahoo
 * @param {string} salonId - Salon identifier
 */
export const signInWithYahoo = async (salonId) => {
  try {
    const provider = new OAuthProvider('yahoo.com');
    provider.addScope('email');
    provider.addScope('profile');
    const result = await signInWithPopup(auth, provider);

    // Create or update customer profile
    await createOrUpdateCustomerProfile(result.user, salonId);

    return result.user;
  } catch (error) {
    console.error('Error signing in with Yahoo:', error);
    throw error;
  }
};

/**
 * Sign in with Apple
 * @param {string} salonId - Salon identifier
 */
export const signInWithApple = async (salonId) => {
  try {
    const provider = new OAuthProvider('apple.com');
    // Request email and name scopes
    provider.addScope('email');
    provider.addScope('name');
    const result = await signInWithPopup(auth, provider);

    // Create or update customer profile
    await createOrUpdateCustomerProfile(result.user, salonId);

    return result.user;
  } catch (error) {
    console.error('Error signing in with Apple:', error);
    throw error;
  }
};

/**
 * Link an existing account with a social provider
 * @param {string} provider - Provider name ('google', 'facebook', 'apple')
 */
export const linkAccountWithProvider = async (provider) => {
  try {
    if (!auth.currentUser) {
      throw new Error('No authenticated user found');
    }

    let authProvider;

    switch (provider) {
      case 'google':
        authProvider = new GoogleAuthProvider();
        authProvider.setCustomParameters({ prompt: 'select_account' });
        break;
      case 'facebook':
        authProvider = new FacebookAuthProvider();
        authProvider.setCustomParameters({ auth_type: 'reauthenticate' });
        break;
      case 'apple':
        authProvider = new OAuthProvider('apple.com');
        authProvider.addScope('email');
        authProvider.addScope('name');
        break;
      default:
        throw new Error(`Unsupported provider: ${provider}`);
    }

    const result = await linkWithPopup(auth.currentUser, authProvider);
    return result.user;
  } catch (error) {
    console.error(`Error linking account with ${provider}:`, error);
    throw error;
  }
};

/**
 * Create or update customer profile in Firestore
 * @param {Object} user - Firebase Auth user object
 * @param {string} salonId - Salon identifier
 */
export const createOrUpdateCustomerProfile = async (user, salonId) => {
  try {
    const email = user.email?.toLowerCase();
    if (!email) {
      throw new Error('User email is required');
    }

    // Use email_salonId as the document ID for customer profile
    const customerId = createCustomerId(email, salonId);
    const customerRef = doc(db, 'customers', customerId);

    // Check if customer profile exists
    const customerDoc = await getDoc(customerRef);

    const providerData = user.providerData.map(provider => ({
      providerId: provider.providerId,
      uid: provider.uid
    }));

    if (customerDoc.exists()) {
      // Update existing customer profile
      await updateDoc(customerRef, {
        uid: user.uid,
        email,
        salonId,
        displayName: user.displayName || customerDoc.data().displayName || email.split('@')[0],
        photoURL: user.photoURL || customerDoc.data().photoURL,
        phoneNumber: user.phoneNumber || customerDoc.data().phoneNumber,
        providerData,
        emailVerified: user.emailVerified || customerDoc.data().emailVerified || false,
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } else {
      // Create new customer profile
      await setDoc(customerRef, {
        uid: user.uid,
        email,
        salonId,
        displayName: user.displayName || email.split('@')[0],
        photoURL: user.photoURL || null,
        phoneNumber: user.phoneNumber || null,
        providerData,
        emailVerified: user.emailVerified || false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        appointments: [],
        metrics: {
          totalSpent: 0,
          pointsEarned: 0,
          pointsRedeemed: 0,
          pointsBalance: 0,
          walletBalance: 0,
          lastTransaction: null
        },
        preferences: {
          notifications: {
            email: true,
            sms: false
          }
        }
      });

      // Auto-assign vouchers from promotions targeting all customers (non-blocking)
      assignVouchersForNewCustomer(salonId, customerId, email)
        .catch(err => console.error('Auto-assign vouchers failed (non-blocking):', err));
    }

    return customerId;
  } catch (error) {
    console.error('Error creating/updating customer profile:', error);
    throw error;
  }
};

/**
 * Get customer profile from Firestore
 * @param {string} email - Customer email
 * @param {string} salonId - Salon identifier
 */
export const getCustomerProfile = async (email, salonId) => {
  try {
    if (!email) {
      return null;
    }

    const customerId = createCustomerId(email, salonId);
    const customerRef = doc(db, 'customers', customerId);
    const customerDoc = await getDoc(customerRef);

    if (customerDoc.exists()) {
      return {
        id: customerDoc.id,
        ...customerDoc.data()
      };
    }

    return null;
  } catch (error) {
    console.error('Error getting customer profile:', error);
    throw error;
  }
};

/**
 * Get current customer
 */
export const getCurrentCustomer = async (salonId, user = null) => {
  const currentUser = user || auth.currentUser;

  if (!currentUser) {
    return null;
  }

  try {
    if (salonId) {
      // If salonId is provided, get the specific customer profile
      return await getCustomerProfile(currentUser.email, salonId);
    } else {
      // Otherwise, fetch all customer documents that match this email
      const customersRef = collection(db, 'customers');
      const q = query(customersRef, where('email', '==', currentUser.email.toLowerCase()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      // Return all matching customer profiles (user might be customer of multiple salons)
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    }
  } catch (error) {
    console.error('Error getting current customer:', error);
    throw error;
  }
};

/**
 * Update customer profile
 * @param {string} salonId - Salon identifier
 * @param {Object} profileData - Profile data to update
 */
export const updateCustomerProfile = async (salonId, profileData) => {
  if (!auth.currentUser || !auth.currentUser.email) {
    throw new Error('Not authenticated');
  }

  try {
    const customerId = createCustomerId(auth.currentUser.email, salonId);
    await updateDoc(doc(db, 'customers', customerId), {
      ...profileData,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error updating customer profile:', error);
    throw error;
  }
};

/**
 * Sign out the current customer
 */
export const signOutCustomer = async () => {
  try {
    await signOut(auth);
    localStorage.removeItem(CUSTOMER_EMAIL_KEY);
    localStorage.removeItem('salonId');
    return true;
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Check if user is authenticated as customer
 */
export const isCustomerAuthenticated = async (salonId) => {
  if (!auth.currentUser) {
    return false;
  }

  try {
    // If salonId is provided, check if user is a customer of that salon
    if (salonId) {
      const customerId = createCustomerId(auth.currentUser.email, salonId);
      const customerDoc = await getDoc(doc(db, 'customers', customerId));
      return customerDoc.exists();
    }

    // Otherwise, check if user is a customer of any salon
    const customersRef = collection(db, 'customers');
    const q = query(customersRef, where('email', '==', auth.currentUser.email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking customer authentication:', error);
    return false;
  }
};

/**
 * Get all customers for a salon
 * @param {string} salonId - Salon identifier
 * @param {Object} options - Query options (limit, orderBy, etc.)
 */
export const getSalonCustomers = async (salonId, options = {}) => {
  try {
    const customersRef = collection(db, 'customers');
    let q = query(customersRef, where('salonId', '==', salonId));

    // Apply optional query constraints
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    if (options.orderBy) {
      q = query(q, orderBy(options.orderBy.field, options.orderBy.direction || 'asc'));
    }

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting salon customers:', error);
    throw error;
  }
};

/**
 * Add/update customer metrics (for tracking spending, points, etc.)
 * @param {string} customerId - Customer document ID
 * @param {Object} metrics - Metrics to update
 */
export const updateCustomerMetrics = async (customerId, metrics) => {
  try {
    const customerRef = doc(db, 'customers', customerId);

    await updateDoc(customerRef, {
      'metrics.totalSpent': increment(metrics.amount || 0),
      'metrics.pointsEarned': increment(metrics.pointsEarned || 0),
      'metrics.pointsRedeemed': increment(metrics.pointsRedeemed || 0),
      'metrics.pointsBalance': increment((metrics.pointsEarned || 0) - (metrics.pointsRedeemed || 0)),
      'metrics.lastTransaction': serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error updating customer metrics:', error);
    throw error;
  }
};
